'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Grid,
  Divider,
  Stack,
} from '@terra/ui-styled-system';
import {
  BodyDefault,
  Button,
  FileUpload,
  Progress,
  HeadingItem,
  MetaDefault,
  toasterApi,
  Scroll,
  IconButton,
  Tooltip,
  LegacyDialog,
} from '@terra/ui-design-system';
import {
  TestTube01Icon,
  File01Icon,
  InformationCircleIcon,
  Upload01Icon,
  Wrench01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useQuery } from '@tanstack/react-query';
import { useAppTargetId } from '../../store/projectStore';
import { terraProxyApiClient } from '../../clients/testerClient';
import { EmptyState } from '../EmptyState';
import { useTranslations } from 'next-intl';

const maxFileSize = 10 * 1024 * 1024; // 10MB
const acceptedFileTypes = '.txt,.doc,.docx,.pdf,.vtt';
const maxFiles = 3;

export function ScopingContent() {
  const tScoping = useTranslations('settings.scopingActions');
  const appTargetId = useAppTargetId();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const progressInterval = useRef<NodeJS.Timeout | undefined>(undefined);
  const {
    data: scopeData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['application-scoping', appTargetId],
    queryFn: async () => {
      if (!appTargetId) {
        throw new Error('No target ID found');
      }

      const result =
        await terraProxyApiClient.getApplicationScoping(appTargetId);

      return result;
    },
    enabled: !!appTargetId,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });
  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || isUploading || !appTargetId) return;
      if (files.length > maxFiles) {
        toasterApi.error({
          title: 'Maximum files reached',
          description: `You can only upload up to ${maxFiles} files at a time`,
        });

        return;
      }

      setIsUploading(true);
      setUploadProgress(0);
      progressInterval.current = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 98) {
            clearInterval(progressInterval.current);

            return 98;
          }

          return prev + Math.random() * 10;
        });
      }, 500);

      try {
        await terraProxyApiClient.uploadApplicationScoping(
          appTargetId,
          Array.from(files),
        );
        clearInterval(progressInterval.current);
        setUploadProgress(100);
        toasterApi.success({
          title: 'Upload successful',
          description: 'Your files have been uploaded successfully',
        });
        setIsUploadDialogOpen(false);
        refetch();
      } catch (error) {
        console.error('Failed to upload file:', error);
        toasterApi.error({
          title: 'Failed to upload file',
          description: 'Error: ' + error,
        });
      } finally {
        clearInterval(progressInterval.current);
        setUploadProgress(0);
        setIsUploading(false);
      }
    },
    [appTargetId, isUploading, refetch],
  );

  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, []);
  if (isLoading) {
    return (
      <EmptyState
        icon={File01Icon}
        title="Loading scoping data..."
        isLoading={true}
      />
    );
  }

  // Check if there's any meaningful content to display
  const hasContent =
    scopeData &&
    (scopeData.fullApplicationDescription ||
      scopeData.webApplicationName ||
      scopeData.webApplicationUrl ||
      scopeData.desiredStartDate ||
      scopeData.desiredFinishDate ||
      scopeData.serverSideLanguage ||
      scopeData.frameworkName ||
      (scopeData.clientSideTechnologies &&
        scopeData.clientSideTechnologies.length > 0) ||
      (scopeData.databases && scopeData.databases.length > 0) ||
      scopeData.cloudProvider ||
      scopeData.testingEnvironment ||
      (scopeData.authenticationMechanisms &&
        scopeData.authenticationMechanisms.length > 0) ||
      (scopeData.outOfScopeFeatures &&
        scopeData.outOfScopeFeatures.length > 0));

  if (!scopeData || !hasContent) {
    return (
      <Box w="full" h="full" display="flex" flexDirection="column">
        {/* Toolbar */}
        <Box
          p="1"
          borderBottomWidth="1px"
          borderColor="border.default"
          bg="bg.subtle"
        >
          <HStack gap="1" alignItems="center" justifyContent="flex-end">
            <Tooltip title={tScoping('uploadFiles')}>
              <IconButton
                size="xs"
                variant="ghost"
                onClick={() => setIsUploadDialogOpen(true)}
                disabled={isUploading}
                aria-label={tScoping('uploadFiles')}
              >
                <Upload01Icon size={12} />
              </IconButton>
            </Tooltip>
          </HStack>
        </Box>

        {/* Progress Bar */}
        {isUploading && (
          <Box p="2" borderBottomWidth="1px" borderColor="border.default">
            <Progress value={uploadProgress} max={100} size="sm" />
          </Box>
        )}

        {/* Empty State */}
        <EmptyState
          icon={File01Icon}
          title="No Scoping Data"
          description="Upload application files to generate a security scope summary for this application"
        />

        {/* Upload Dialog */}
        <UploadDialog
          isOpen={isUploadDialogOpen}
          onClose={() => setIsUploadDialogOpen(false)}
          onFileUpload={handleFileUpload}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
        />
      </Box>
    );
  }

  return (
    <Box w="full" h="full" display="flex" flexDirection="column">
      {/* Toolbar */}
      <Box
        p="1"
        borderBottomWidth="1px"
        borderColor="border.default"
        bg="bg.subtle"
      >
        <HStack gap="1" alignItems="center" justifyContent="flex-end">
          <Tooltip title={tScoping('uploadFiles')}>
            <IconButton
              size="xs"
              variant="ghost"
              onClick={() => setIsUploadDialogOpen(true)}
              disabled={isUploading}
              aria-label={tScoping('uploadFiles')}
            >
              <Upload01Icon size={12} />
            </IconButton>
          </Tooltip>
        </HStack>
      </Box>

      {/* Progress Bar */}
      {isUploading && (
        <Box p="2" borderBottomWidth="1px" borderColor="border.default">
          <Progress value={uploadProgress} max={100} size="sm" />
        </Box>
      )}

      {/* Content */}
      <Scroll orientation="vertical" showScrollbar={true} flex="1">
        <Box p="6" w="full">
          <VStack gap="6" w="full">
            {/* Description Section */}
            {scopeData?.fullApplicationDescription && (
              <Box w="full">
                <HStack gap="2" mb="3">
                  <InformationCircleIcon size={16} />
                  <HeadingItem>Description</HeadingItem>
                </HStack>
                <BodyDefault color="fg.default">
                  {scopeData.fullApplicationDescription}
                </BodyDefault>
              </Box>
            )}

            <Divider />

            {/* Details Grid */}
            <Grid columns={{ base: 1, md: 2, lg: 3 }} gap="8" w="full">
              {/* Project Information */}
              <VStack gap="4" alignItems="flex-start">
                <HStack gap="2">
                  <File01Icon size={14} />
                  <HeadingItem>Project Information</HeadingItem>
                </HStack>
                <VStack gap="3" alignItems="flex-start" w="full">
                  {scopeData.webApplicationName && (
                    <InfoField
                      label="Application Name"
                      value={scopeData.webApplicationName}
                    />
                  )}
                  {scopeData.webApplicationUrl && (
                    <InfoField
                      label="Application URL"
                      value={scopeData.webApplicationUrl}
                    />
                  )}
                  {scopeData.desiredStartDate && (
                    <InfoField
                      label="Start Date"
                      value={scopeData.desiredStartDate}
                    />
                  )}
                  {scopeData.desiredFinishDate && (
                    <InfoField
                      label="End Date"
                      value={scopeData.desiredFinishDate}
                    />
                  )}
                </VStack>
              </VStack>

              {/* Technical Details */}
              <VStack gap="4" alignItems="flex-start">
                <HStack gap="2">
                  <Wrench01Icon size={14} />
                  <HeadingItem>Technical Details</HeadingItem>
                </HStack>
                <VStack gap="3" alignItems="flex-start" w="full">
                  {scopeData.serverSideLanguage && (
                    <InfoField
                      label="Server Language"
                      value={scopeData.serverSideLanguage}
                    />
                  )}
                  {scopeData.frameworkName && (
                    <InfoField
                      label="Framework"
                      value={scopeData.frameworkName}
                    />
                  )}
                  {scopeData.clientSideTechnologies &&
                    scopeData.clientSideTechnologies.length > 0 && (
                      <InfoField
                        label="Client Technologies"
                        value={scopeData.clientSideTechnologies.join(', ')}
                      />
                    )}
                  {scopeData.databases && scopeData.databases.length > 0 && (
                    <InfoField
                      label="Databases"
                      value={scopeData.databases.join(', ')}
                    />
                  )}
                  {scopeData.cloudProvider && (
                    <InfoField
                      label="Cloud Provider"
                      value={scopeData.cloudProvider}
                    />
                  )}
                </VStack>
              </VStack>

              {/* Testing Details */}
              <VStack gap="4" alignItems="flex-start">
                <HStack gap="2">
                  <TestTube01Icon size={14} />
                  <HeadingItem>Testing Details</HeadingItem>
                </HStack>
                <VStack gap="3" alignItems="flex-start" w="full">
                  {scopeData.testingEnvironment && (
                    <InfoField
                      label="Environment"
                      value={scopeData.testingEnvironment}
                    />
                  )}
                  {scopeData.authenticationMechanisms &&
                    scopeData.authenticationMechanisms.length > 0 && (
                      <InfoField
                        label="Authentication"
                        value={scopeData.authenticationMechanisms.join(', ')}
                      />
                    )}
                  {scopeData.outOfScopeFeatures &&
                    scopeData.outOfScopeFeatures.length > 0 && (
                      <InfoField
                        label="Out of Scope"
                        value={scopeData.outOfScopeFeatures.join(', ')}
                      />
                    )}
                </VStack>
              </VStack>
            </Grid>
          </VStack>
        </Box>
      </Scroll>

      {/* Upload Dialog */}
      <UploadDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onFileUpload={handleFileUpload}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />
    </Box>
  );
}

function InfoField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <Box w="full">
      <MetaDefault color="fg.muted" mb="1">
        {label}
      </MetaDefault>
      <BodyDefault color="fg.default">{value || 'N/A'}</BodyDefault>
    </Box>
  );
}

function UploadDialog({
  isOpen,
  onClose,
  onFileUpload,
  isUploading,
  uploadProgress,
}: {
  isOpen: boolean;
  onClose: () => void;
  onFileUpload: (files: FileList | null) => void;
  isUploading: boolean;
  uploadProgress: number;
}) {
  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content>
          <Stack gap="8" p="6">
            <Stack gap="1">
              <LegacyDialog.Title>Upload Scoping Files</LegacyDialog.Title>
              <LegacyDialog.Description>
                Upload application files to generate a project scope summary.
              </LegacyDialog.Description>
            </Stack>

            <FileUpload.Root
              maxFiles={maxFiles}
              accept={acceptedFileTypes}
              maxFileSize={maxFileSize}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onFileUpload(e.target.files)
              }
              disabled={isUploading}
            >
              <FileUpload.Dropzone>
                <VStack gap="3">
                  <FileUpload.Label fontSize="sm">
                    {isUploading
                      ? 'Uploading...'
                      : 'Drop your files here or click to browse'}
                  </FileUpload.Label>
                  {!isUploading && (
                    <FileUpload.Trigger asChild>
                      <Button size="sm" variant="outline">
                        Choose Files
                      </Button>
                    </FileUpload.Trigger>
                  )}
                  {isUploading && (
                    <Box w="full">
                      <Progress value={uploadProgress} max={100} size="sm" />
                    </Box>
                  )}
                  <VStack gap="1">
                    <MetaDefault color="fg.muted">
                      Accepted: {acceptedFileTypes || 'All files'}
                    </MetaDefault>
                    <MetaDefault color="fg.muted">
                      Max {maxFiles} files, up to {maxFileSize / 1024 / 1024}MB
                      each
                    </MetaDefault>
                  </VStack>
                </VStack>
              </FileUpload.Dropzone>
              <FileUpload.HiddenInput />
            </FileUpload.Root>
          </Stack>

          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <IconButton aria-label="Close Dialog" variant="ghost" size="sm">
              <Cancel01Icon />
            </IconButton>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
